/* eslint-disable import/no-named-as-default */
import React from 'react';
import classNames from 'classnames';

import dispatchNewEvent from '../../utils/analytics/dispatchEvent';
import postMonetateImpression from '../../utils/monetate/postMonetateImpression';
import { TopLinks } from '../link-list/LinkList';
import { topLinks } from '../links';
import Logo from '../logo/Logo';
import UserTools from '../user-tools/UserTools';
import Search from '../search/Search';
import TypeaheadOverlay from '../typeahead-overlay/TypeaheadOverlay';
import {
  desktopMediaQuery,
  isDesktop,
} from '../../utils/device/identifyDevice';
import isMonetateGroup from '../../utils/monetate/isMonetateGroup';
import EnvVarsFeaturesContext from '../../scaffold/EnvVarsFeaturesContext';

import styles from './responsive-header.scss';

const logoMaxHeight = 70;
const minHeightOfLogoTextLegible = 45;
const brandmarkHeight = 36;
const peepHideThreshold = 150;
const peepShowThreshold = 100;

export class ResponsiveHeader extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showNav: false,
      swapLogo: false,
      previousScrollPosition: 0,
      wasScrollingUp: false,
      previousDirectionChange: 0,
      isVisible: true,
      isDesktop: false,
      isFocused: false,
    };
    const { features, envVars } = this.context;
    this.peepHeader = features.peepHeader;
    this.monetateExperiences = features.monetateExperiences;
    this.monetate = envVars.monetate;
    this.slimMobileHeader = isMonetateGroup(features, 'slim-mobile-header');
    this.handleDeviceOrientation = this.handleDeviceOrientation.bind(this);
    this.updateView = this.updateView.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.notifyMonetate = this.notifyMonetate.bind(this);
    this.searchInputFocus = this.searchInputFocus.bind(this);
    this.escKeyHandler = this.escKeyHandler.bind(this);
    this.searchInputBlur = this.searchInputBlur.bind(this);
    this.fullpageTypeahead = isMonetateGroup(features, 'fullpage-typeahead');
    this.handleUnpeep = this.handleUnpeep.bind(this);
    this.scrollbarWidth = 0;
    this.header = React.createRef();
    this.headerContainer = React.createRef();
  }

  componentDidMount() {
    this.scrollbarWidth = window.innerWidth - document.body.clientWidth;
    this.updateView();
    this.updateHeight();
    window.addEventListener('scroll', this.updateView);
    window.addEventListener('resize', this.updateHeight);
    window.addEventListener('keydown', this.escKeyHandler);

    this.handleDeviceOrientation();
    window.addEventListener(
      'jl-events.browser.mobileHeaderMenuClick',
      this.notifyMonetate,
      { once: true }
    );
    window.addEventListener(
      'jl-events.browser.mobileHeaderCloseMenu',
      this.onMenuButtonClick
    );
    window.addEventListener(
      'jl-events.browser.mobileHeaderUnpeep',
      this.handleUnpeep
    );
    desktopMediaQuery().addListener(this.handleDeviceOrientation);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updateView);
    window.removeEventListener('resize', this.updateHeight);
    window.removeEventListener('keydown', this.escKeyHandler);
    window.removeEventListener(
      'jl-events.browser.mobileHeaderMenuClick',
      this.notifyMonetate,
      { once: true }
    );
    window.removeEventListener(
      'jl-events.browser.mobileHeaderCloseMenu',
      this.onMenuButtonClick
    );
    window.removeEventListener(
      'jl-events.browser.mobileHeaderUnpeep',
      this.handleUnpeep
    );
  }

  onMenuButtonClick(e) {
    const showNav = !this.state.showNav;
    e.preventDefault();

    this.setState({ showNav }, this.updateLogo);
    this.props.updateMobileNavState(showNav);

    const menuClick = new CustomEvent(
      'jl-events.browser.mobileHeaderMenuClick',
      {
        detail: {
          hide: showNav,
        },
      }
    );

    window.dispatchEvent(menuClick);

    if (showNav) {
      window.document.body.classList.add('lockBody');
    } else {
      window.document.body.classList.remove('lockBody');
    }

    this.triggerMenuButtonAnalytics();
  }

  setVisibility() {
    const {
      previousScrollPosition,
      previousDirectionChange,
      wasScrollingUp,
      isVisible,
      showNav,
    } = this.state;

    if (isDesktop()) {
      this.setState({ isVisible: true });
      return;
    }

    const currentScrollPosition = window.pageYOffset;
    const isCurrentlyScrollingUp =
      currentScrollPosition < previousScrollPosition;
    const newVisibilityState = {};

    const scrollDirectionDidChange = () =>
      wasScrollingUp !== isCurrentlyScrollingUp;

    const scrollPositionOffTopOfPage = () => currentScrollPosition < 0;

    const headerAtTopOfScreen = () => currentScrollPosition < peepHideThreshold;

    const updatePreviousDirectionChange = () => {
      if (scrollDirectionDidChange()) {
        // if nav is open or user has scrolled off the top of the page
        // don't update previousDirectionChange as it will set the value to 0 or less
        return scrollPositionOffTopOfPage() || showNav
          ? previousDirectionChange
          : currentScrollPosition;
      }
      return previousDirectionChange;
    };

    const scrolledUpPastPeepThreshold = () => {
      const showThreshold = previousDirectionChange - peepShowThreshold;

      return (
        !isVisible &&
        isCurrentlyScrollingUp &&
        currentScrollPosition < showThreshold
      );
    };

    const scrolledDownPastPeepThreshold = () => {
      const hideThreshold = previousDirectionChange + peepHideThreshold;

      return (
        isVisible &&
        !isCurrentlyScrollingUp &&
        currentScrollPosition > hideThreshold
      );
    };

    const showHeader = () => {
      newVisibilityState.isVisible = true;
      this.dispatchPeepEvent(newVisibilityState.isVisible);
    };

    const hideHeader = () => {
      newVisibilityState.isVisible = false;
      this.dispatchPeepEvent(newVisibilityState.isVisible);
    };

    if (scrolledUpPastPeepThreshold() || headerAtTopOfScreen()) {
      showHeader();
    }

    if (scrolledDownPastPeepThreshold()) {
      hideHeader();
    }

    this.setState({
      ...newVisibilityState,
      previousDirectionChange: updatePreviousDirectionChange(),
      wasScrollingUp: isCurrentlyScrollingUp,
      previousScrollPosition: scrollPositionOffTopOfPage()
        ? 0
        : currentScrollPosition,
    });
  }

  triggerMenuButtonAnalytics() {
    dispatchNewEvent('dataStateChange', {
      action: 'topNav - Side Flyout',
      type: this.state.showNav ? 'Closed' : 'Open',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  dispatchPeepEvent(peeped) {
    const event = new CustomEvent('jl-events.browser.mobileHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }

  notifyMonetate() {
    postMonetateImpression(
      this.monetate,
      'burger-menu-opened',
      this.monetateExperiences
    );
    postMonetateImpression(
      this.monetate,
      'remove-dont-miss',
      this.monetateExperiences
    );
  }

  handleUnpeep() {
    if (!isDesktop() && window.pageYOffset >= peepHideThreshold) {
      this.dispatchPeepEvent(false);
      this.setState({ isVisible: false });
    }
  }

  handleDeviceOrientation() {
    const showNav = false;
    let newState = { isDesktop: false };

    if (isDesktop()) {
      newState = { showNav, swapLogo: false, isDesktop: true };
      this.props.updateMobileNavState(showNav);
      window.document.body.classList.remove('lockBody');
    }
    this.setState({ ...newState });
  }

  updateLogo() {
    if (isDesktop()) {
      this.setState({ swapLogo: false });
      return;
    }
    const maxHeight = this.slimMobileHeader ? brandmarkHeight : logoMaxHeight;
    const heightTextLegible = this.slimMobileHeader
      ? brandmarkHeight
      : minHeightOfLogoTextLegible;
    // calculate initial logo height minus amount scrolled
    let logoHeight = maxHeight - window.pageYOffset;
    // account for negative scrolling (upwards)
    // set maximum height of logo to 70px
    logoHeight = logoHeight > maxHeight ? maxHeight : logoHeight;

    const { showNav } = this.state;
    // if nav is open set to brandmark height or
    // if logoHeight is calculated smaller than brandmark height, restrict to brandmark height

    if (showNav || logoHeight <= brandmarkHeight) {
      // ensure brandmark is being shown
      this.setState({ swapLogo: true });
    } else {
      // if logo height is lower than legibility size, show brandmark
      if (logoHeight < heightTextLegible) {
        this.setState({ swapLogo: true });
      }

      // if logo height is higher than legibility size, show full logo
      if (logoHeight > heightTextLegible) {
        this.setState({ swapLogo: false });
      }
    }
  }

  handleOverlayKeyPress(event) {
    if (event.key === 'Enter') {
      this.onMenuButtonClick(event);
    }
  }

  updateView() {
    if (!this.state.isFocused) {
      requestAnimationFrame(() => {
        this.updateLogo();
        this.setVisibility();
      });
    }
  }

  updateHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    requestAnimationFrame(() => {
      if (this?.headerContainer?.current && this?.header?.current) {
        this.headerContainer.current.style.height = this.header.current.scrollHeight;
      }
    });
  }

  searchInputFocus() {
    if (this.fullpageTypeahead) {
      window.scrollTo(0, 0);
      this.setState({ isFocused: true });
      document.body.classList.add(styles.fullpageTypeaheadOpen);
    }
    postMonetateImpression(
      this.monetate,
      'fullpage-typeahead typeahead-content',
      this.monetateExperiences
    );
  }

  escKeyHandler(event) {
    if (event.keyCode === 27) {
      document.activeElement.blur();
      this.searchInputBlur();
    }
  }

  searchInputBlur() {
    if (this.fullpageTypeahead) {
      this.setState({ isFocused: false });
      document.body.classList.remove(styles.fullpageTypeaheadOpen);
    }
  }

  render() {
    const { showNav, swapLogo, isVisible } = this.state;
    const { topNotification, bottomNotification } = this.props;

    const wrapperClasses = classNames(styles.header, {
      [styles.slimHeader]: this.slimMobileHeader,
      [styles.navOpenPersistent]: showNav,
    });

    const overlayClasses = classNames(styles.overlay, {
      [styles.showOverlay]: showNav,
    });

    const splashClasses = classNames(
      this.fullpageTypeahead && this.state.isFocused
        ? styles.expandedSearch
        : styles.search
    );

    const containerClasses = classNames(styles.container, {
      [styles.peepHeader]: this.peepHeader,
      [styles.peep]: this.peepHeader && !isVisible && !showNav,
    });

    return (
      <div
        className={wrapperClasses}
        ref={this.headerContainer}
        style={
          this.state.isFocused
            ? { paddingRight: `${this.scrollbarWidth}px` }
            : undefined
        }
      >
        <div className={containerClasses} ref={this.header}>
          {!showNav && topNotification}
          <div className={styles.topBar}>
            <div className={styles.topLinks}>
              <TopLinks links={topLinks} />
            </div>
          </div>
          <div className={styles.logoRow}>
            <Logo
              showMark={
                swapLogo || (!this.state.isDesktop && this.slimMobileHeader)
              }
              splashScreen={this.state.isFocused}
              slimMobileHeader={this.slimMobileHeader}
            />
            <div
              className={splashClasses}
              id="mobile-search"
              onFocus={this.searchInputFocus}
            >
              <Search
                hideSearch={showNav || (this.peepHeader && !isVisible)}
                id="mobileSearch"
                onFocus={this.searchInputFocus}
              />
            </div>
            {this.state.isFocused ? (
              <TypeaheadOverlay onCloseButtonClick={this.searchInputBlur} />
            ) : (
              <UserTools
                isMenuOpen={showNav}
                onMenuButtonClick={e => this.onMenuButtonClick(e)}
              />
            )}
          </div>
          {this.state.isFocused && (
            <hr
              data-testid="typeahead-separator"
              className={styles.typeaheadSeparator}
            />
          )}
          {!showNav && bottomNotification}
        </div>
        {showNav && (
          <div
            className={overlayClasses}
            onClick={e => this.onMenuButtonClick(e)}
            role="button"
            tabIndex={0}
            onKeyPress={this.handleOverlayKeyPress}
          />
        )}
      </div>
    );
  }
}

ResponsiveHeader.contextType = EnvVarsFeaturesContext;

export default ResponsiveHeader;
